import React from "react";
import { Search } from "lucide-react";

export interface PaymentItem {
  id: string;
  paymentRef: string;
  orderRef: string;
  date: string;
  dateObj?: Date;
  customerName: string;
  method: "Cash" | "Card" | "Gift Card" | "Online" | "Voucher";
  type: "Payment" | "Refund" | "Deposit";
  amount: string;
  status: "Success" | "Failed" | "Voided";
}

interface PaymentsTableProps {
  payments: PaymentItem[];
  isLoading: boolean;
  indexOfFirstItem: number;
}

export const PaymentsTable: React.FC<PaymentsTableProps> = ({
  payments,
  isLoading,
  indexOfFirstItem,
}) => {
  return (
    <div className="flex-1 lg:overflow-auto relative">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
          <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
            <th className="col-span-1 text-left font-bold whitespace-nowrap">#</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Date</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Payment Ref</th>
            <th className="col-span-1 text-left font-bold whitespace-nowrap">Order Ref</th>
            <th className="col-span-2 text-left font-bold whitespace-nowrap">Customer</th>
            <th className="col-span-1 text-left font-bold whitespace-nowrap">Method</th>
            <th className="col-span-1 text-left font-bold whitespace-nowrap">Type</th>
            <th className="col-span-1 text-center font-bold whitespace-nowrap">Status</th>
            <th className="col-span-1 text-right font-bold whitespace-nowrap">Amount</th>
          </tr>
        </thead>
        <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0 p-4 lg:p-0">
          {isLoading ? (
            <tr className="h-64 w-full flex items-center justify-center lg:table-row">
              <td colSpan={12} className="h-full">
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                </div>
              </td>
            </tr>
          ) : payments.length === 0 ? (
            <tr className="flex flex-col items-center justify-center h-64 text-gray-400 w-full lg:table-row">
              <td colSpan={12} className="w-full h-full p-0">
                <div className="flex flex-col items-center justify-center h-full w-full">
                  <Search className="w-12 h-12 mb-3 opacity-20" />
                  <p className="text-lg font-medium text-gray-500">
                    No payment records found
                  </p>
                  <p className="text-sm">Try adjusting your search terms</p>
                </div>
              </td>
            </tr>
          ) : (
            <>
              {payments.map((payment, idx) => (
                <tr
                  key={payment.id}
                  className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-4 items-stretch lg:items-center px-5 py-4 lg:px-6 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full"
                >
                  <td className="lg:col-span-1 text-gray-400 font-bold lg:font-medium flex justify-between items-center lg:block">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Record #</span>
                    <span>{indexOfFirstItem + idx + 1}</span>
                  </td>

                  <td className="lg:col-span-2 font-bold lg:font-medium text-gray-900 lg:text-gray-800 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Date</span>
                    <span className="truncate">{payment.date}</span>
                  </td>

                  <td className="lg:col-span-2 font-mono text-xs text-gray-500 lg:text-gray-600 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold font-sans">Payment Ref</span>
                    <span className="bg-gray-100 lg:bg-transparent px-1.5 py-0.5 rounded lg:p-0">{payment.paymentRef}</span>
                  </td>

                  <td className="lg:col-span-1 font-mono text-xs text-gray-500 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold font-sans">Order Ref</span>
                    <span className="bg-gray-100 lg:bg-transparent px-1.5 py-0.5 rounded lg:p-0">{payment.orderRef}</span>
                  </td>

                  <td className="lg:col-span-2 font-bold lg:font-medium text-gray-900 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Customer</span>
                    <span className="truncate">{payment.customerName}</span>
                  </td>

                  <td className="lg:col-span-1 text-gray-600 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Method</span>
                    <span className="font-medium lg:font-normal">{payment.method}</span>
                  </td>

                  <td className="lg:col-span-1 flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Type</span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] lg:text-xs font-bold lg:font-medium whitespace-nowrap ${payment.type === "Payment"
                          ? "bg-green-100 text-green-800"
                          : payment.type === "Refund"
                            ? "bg-red-100 text-red-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                    >
                      {payment.type}
                    </span>
                  </td>

                  <td className="lg:col-span-1 text-center flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Status</span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 lg:py-1 rounded-md text-[10px] lg:text-xs font-bold lg:font-medium whitespace-nowrap ${payment.status === "Success"
                          ? "bg-green-50 text-green-700 border border-green-100"
                          : payment.status === "Voided"
                            ? "bg-gray-50 text-gray-700 border border-gray-100"
                            : "bg-red-50 text-red-700 border border-red-100"
                        }`}
                    >
                      {payment.status}
                    </span>
                  </td>

                  <td className={`lg:col-span-1 text-right font-extrabold lg:font-bold flex justify-between items-center lg:block border-t border-gray-50 lg:border-0 pt-3 lg:pt-0 ${payment.type === 'Refund' ? 'text-red-600' : 'text-[#885ABB] lg:text-gray-900'}`}>
                    <span className="lg:hidden text-[10px] uppercase tracking-wider text-gray-400 font-bold">Amount</span>
                    <span className="text-base lg:text-sm">{payment.type === 'Refund' ? '-' : ''}{payment.amount}</span>
                  </td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};
